import React, { useState } from 'react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Phone, Star, ShieldCheck, CheckCircle, ArrowRight, Menu, X, MapPin, Droplets, Sun, Building2, Home } from 'lucide-react';
export function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return <div className="min-h-screen bg-background text-foreground font-sans selection:bg-primary/30" data-id="element-0">
      {/* Navigation */}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" data-id="element-1">
        <div className="container mx-auto px-4 h-20 flex items-center justify-between" data-id="element-2">
          <div className="flex items-center gap-2" data-id="element-3">
            <img src="https://cdn.magicpatterns.com/uploads/akHq6rnmt4As3JRquQqvbk/sparkle-tech-window-washing.png-1-e1707734280501.webp" alt="Sparkle Tech Window Washing Logo" className="h-12 w-auto object-contain brightness-150 contrast-125" data-id="element-4" />
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-id="element-5">
            <a href="#services" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors" data-id="element-6">
              Services
            </a>
            <a href="#portfolio" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors" data-id="element-7">
              Portfolio
            </a>
            <a href="#about" className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors" data-id="element-8">
              About
            </a>
            <div className="flex items-center gap-4 ml-4" data-id="element-9">
              <a href="tel:18003703998" className="flex items-center gap-2 text-sm font-bold text-primary hover:text-accent transition-colors" data-id="element-10">
                <Phone className="h-4 w-4" data-id="element-11" />
                1-800-370-3998
              </a>
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold tracking-wide" data-id="element-12">
                <a href="#quote" data-id="element-13">Get a Free Quote</a>
              </Button>
            </div>
          </nav>

          {/* Mobile Menu Toggle */}
          <button className="md:hidden p-2 text-foreground" onClick={() => setIsMenuOpen(!isMenuOpen)} data-id="element-14">
            {isMenuOpen ? <X className="h-6 w-6" data-id="element-15" /> : <Menu className="h-6 w-6" data-id="element-16" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {isMenuOpen && <div className="md:hidden border-t border-border/40 bg-background/95 backdrop-blur px-4 py-6 flex flex-col gap-4" data-id="element-17">
            <a href="#services" className="text-lg font-medium" onClick={() => setIsMenuOpen(false)} data-id="element-18">
              Services
            </a>
            <a href="#portfolio" className="text-lg font-medium" onClick={() => setIsMenuOpen(false)} data-id="element-19">
              Portfolio
            </a>
            <a href="#about" className="text-lg font-medium" onClick={() => setIsMenuOpen(false)} data-id="element-20">
              About
            </a>
            <div className="h-px bg-border/40 my-2" data-id="element-21" />
            <a href="tel:18003703998" className="flex items-center gap-2 text-lg font-bold text-primary" data-id="element-22">
              <Phone className="h-5 w-5" data-id="element-23" />
              1-800-370-3998
            </a>
            <Button asChild className="w-full mt-2 bg-primary text-primary-foreground font-bold" data-id="element-24">
              <a href="#quote" onClick={() => setIsMenuOpen(false)} data-id="element-25">
                Get a Free Quote
              </a>
            </Button>
          </div>}
      </header>

      <main data-id="element-26">
        {/* Hero Section */}
        <section className="relative pt-24 pb-32 overflow-hidden" data-id="element-27">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0 z-0" data-id="element-28">
            <img src="https://cdn.magicpatterns.com/uploads/9JEZFtqeC5RoiireV6Hprn/sparkle4.jpg" alt="Luxury home with clean windows" className="w-full h-full object-cover opacity-20" data-id="element-29" />
            <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/95 to-background" data-id="element-30" />
            <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" data-id="element-31" />
          </div>

          <div className="container mx-auto px-4 relative z-10" data-id="element-32">
            <div className="max-w-3xl mx-auto text-center md:mx-0 md:text-left" data-id="element-33">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-6" data-id="element-34">
                <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 text-sm py-1" data-id="element-35">
                  <ShieldCheck className="h-4 w-4 mr-1" data-id="element-36" /> BBB Accredited
                </Badge>
                <Badge variant="secondary" className="bg-accent/10 text-accent border-accent/20 text-sm py-1" data-id="element-37">
                  <Star className="h-4 w-4 mr-1" data-id="element-38" /> 16+ Years Experience
                </Badge>
              </div>

              <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.1] pb-2 mb-6 text-transparent bg-clip-text bg-gradient-to-br from-white via-gray-300 to-gray-500" data-id="element-39">
                Unrivalled Quality, <br data-id="element-40" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent" data-id="element-41">
                  Phoenix Metro
                </span>{' '}
                <br data-id="element-42" />
                Window Cleaning
              </h1>

              <p className="text-xl md:text-2xl text-muted-foreground mb-10 max-w-2xl mx-auto md:mx-0 leading-relaxed" data-id="element-43">
                Experience sparkling views with professional window cleaning. We
                transform your home or workspace with streak-free, luminous
                windows.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start" data-id="element-44">
                <Button size="lg" asChild className="text-lg h-14 px-8 bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_0_30px_-5px_rgba(56,189,248,0.4)]" data-id="element-45">
                  <a href="#quote" className="inline-flex items-center justify-center gap-2" data-id="element-46">
                    Get Your Free Estimate
                    <ArrowRight className="h-5 w-5" data-id="element-47" />
                  </a>
                </Button>
                <Button size="lg" variant="outline" asChild className="text-lg h-14 px-8 border-border hover:bg-secondary/50" data-id="element-48">
                  <a href="tel:18003703998" className="inline-flex items-center justify-center gap-2" data-id="element-49">
                    <Phone className="h-5 w-5 text-primary" data-id="element-50" /> Call Now
                  </a>
                </Button>
              </div>

              <div className="mt-12 flex flex-wrap items-center justify-center md:justify-start gap-6 text-sm text-muted-foreground" data-id="element-51">
                <div className="flex items-center gap-2" data-id="element-52">
                  <CheckCircle className="h-5 w-5 text-primary" data-id="element-53" /> Fully Insured
                </div>
                <div className="flex items-center gap-2" data-id="element-54">
                  <CheckCircle className="h-5 w-5 text-primary" data-id="element-55" /> Satisfaction
                  Guaranteed
                </div>
                <div className="flex items-center gap-2" data-id="element-56">
                  <CheckCircle className="h-5 w-5 text-primary" data-id="element-57" /> Free
                  Estimates
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-24 bg-card/30 border-y border-border/50 relative" data-id="element-58">
          <div className="container mx-auto px-4" data-id="element-59">
            <div className="text-center max-w-2xl mx-auto mb-16" data-id="element-60">
              <h2 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-61">
                Premium Cleaning Services
              </h2>
              <p className="text-muted-foreground text-lg" data-id="element-62">
                Comprehensive exterior cleaning solutions tailored for luxury
                properties across the Phoenix metro area.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-63">
              <Card className="bg-background/50 border-border/50 hover:border-primary/50 transition-colors group" data-id="element-64">
                <CardHeader data-id="element-65">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors" data-id="element-66">
                    <Droplets className="h-6 w-6 text-primary" data-id="element-67" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-68">Window Cleaning</CardTitle>
                  <CardDescription className="text-base" data-id="element-69">
                    Streak-free interior and exterior window cleaning for
                    residential and commercial properties. Advanced techniques
                    remove dirt, grime, and hard water spots for a crystal-clear
                    view.
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="bg-background/50 border-border/50 hover:border-primary/50 transition-colors group" data-id="element-70">
                <CardHeader data-id="element-71">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors" data-id="element-72">
                    <Building2 className="h-6 w-6 text-primary" data-id="element-73" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-74">
                    Window Screen Services
                  </CardTitle>
                  <CardDescription className="text-base" data-id="element-75">
                    Complete screen services: new screen installation,
                    rescreening, screen repair, bug screens, sun and solar
                    screens, slider screens, and screened-in patio rescreening.
                    Fast same-day and same-week service available.
                  </CardDescription>
                </CardHeader>
              </Card>

              <Card className="bg-background/50 border-border/50 hover:border-primary/50 transition-colors group" data-id="element-76">
                <CardHeader data-id="element-77">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors" data-id="element-78">
                    <Sun className="h-6 w-6 text-primary" data-id="element-79" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-80">
                    Solar Panel Cleaning
                  </CardTitle>
                  <CardDescription className="text-base" data-id="element-81">
                    Safe, professional solar panel cleaning that removes dust
                    and buildup so your panels run at peak efficiency, with
                    strict safety protocols to protect your system.
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </section>

        {/* Portfolio / Gallery Section */}
        <section id="portfolio" className="py-24" data-id="element-82">
          <div className="container mx-auto px-4" data-id="element-83">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6" data-id="element-84">
              <div className="max-w-2xl" data-id="element-85">
                <h2 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-86">
                  Our Recent Work
                </h2>
                <p className="text-muted-foreground text-lg" data-id="element-87">
                  See the difference professional window cleaning makes.
                  Streak-free results that let the Arizona sun shine through.
                </p>
              </div>
              <Button variant="outline" className="shrink-0 border-border hover:bg-secondary/50" data-id="element-88">
                View Full Portfolio
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-89">
              <div className="group relative overflow-hidden rounded-xl aspect-[4/3]" data-id="element-90">
                <img src="https://cdn.magicpatterns.com/uploads/mHyb4B9xJ6SZR4oRRvcCSE/sparkle3.jpg" alt="Clean windows reflecting desert landscape" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-91" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6" data-id="element-92">
                  <span className="text-white font-medium text-lg" data-id="element-93">
                    Residential Exterior
                  </span>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-xl aspect-[4/3]" data-id="element-94">
                <img src="https://cdn.magicpatterns.com/uploads/t9XuxyYkNjKwJboR462QpA/sparkle2.jpg" alt="Large clean window with green shade" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-95" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6" data-id="element-96">
                  <span className="text-white font-medium text-lg" data-id="element-97">
                    Custom Glass Features
                  </span>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-xl aspect-[4/3] md:col-span-2 lg:col-span-1" data-id="element-98">
                <img src="https://cdn.magicpatterns.com/uploads/aywSq3WkZXjkk9aLmAgya5/sparkle1.jpg" alt="Clean windows reflecting sky" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-99" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6" data-id="element-100">
                  <span className="text-white font-medium text-lg" data-id="element-101">
                    Architectural Windows
                  </span>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-xl aspect-[4/3]" data-id="element-102">
                <img src="https://cdn.magicpatterns.com/uploads/by9dbVSVGFLFYoEsMShn2C/sparkle7.jpg" alt="Home bay window with fresh sun screens installed" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-103" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6" data-id="element-104">
                  <span className="text-white font-medium text-lg" data-id="element-105">
                    Sun Screen Installation
                  </span>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-xl aspect-[4/3]" data-id="element-106">
                <img src="https://cdn.magicpatterns.com/uploads/n2uze6EEezz8qJQc8tyMzS/sparkle6.jpg" alt="Freshly cleaned rooftop solar panel array" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-107" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6" data-id="element-108">
                  <span className="text-white font-medium text-lg" data-id="element-109">
                    Solar Panel Cleaning
                  </span>
                </div>
              </div>
              <div className="group relative overflow-hidden rounded-xl aspect-[4/3]" data-id="element-110">
                <img src="https://cdn.magicpatterns.com/uploads/gZNd8jayHdPuLbkvDfAF7b/sparkle5.jpg" alt="Window screens rescreened on a bay window" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-111" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6" data-id="element-112">
                  <span className="text-white font-medium text-lg" data-id="element-113">
                    Window Screen Services
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Lead Gen / Contact Section */}
        <section id="quote" className="py-24 bg-card/50 border-t border-border/50 relative overflow-hidden" data-id="element-114">
          {/* Decorative glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" data-id="element-115" />

          <div className="container mx-auto px-4 relative z-10" data-id="element-116">
            <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-117">
              <div data-id="element-118">
                <Badge variant="outline" className="mb-6 border-primary/30 text-primary" data-id="element-119">
                  Get Started Today
                </Badge>
                <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-120">
                  Ready for a clearer view?
                </h2>
                <p className="text-xl text-muted-foreground mb-8" data-id="element-121">
                  Fill out the form to request your free, no-obligation
                  estimate. We typically respond within 2 hours during business
                  hours.
                </p>

                <div className="space-y-6" data-id="element-122">
                  <div className="flex items-start gap-4" data-id="element-123">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-1" data-id="element-124">
                      <Phone className="h-5 w-5 text-primary" data-id="element-125" />
                    </div>
                    <div data-id="element-126">
                      <h4 className="font-semibold text-lg" data-id="element-127">
                        Call Us Directly
                      </h4>
                      <p className="text-muted-foreground mb-1" data-id="element-128">
                        Available Mon-Sat, 8am-6pm
                      </p>
                      <a href="tel:18003703998" className="text-xl font-bold text-primary hover:text-accent transition-colors" data-id="element-129">
                        1-800-370-3998
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-id="element-130">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-1" data-id="element-131">
                      <MapPin className="h-5 w-5 text-primary" data-id="element-132" />
                    </div>
                    <div data-id="element-133">
                      <h4 className="font-semibold text-lg" data-id="element-134">Service Area</h4>
                      <p className="text-muted-foreground" data-id="element-135">
                        Based in Peoria, proudly serving the greater Phoenix
                        metro area and statewide.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <Card className="bg-background/80 backdrop-blur border-border/50 shadow-2xl" data-id="element-136">
                <CardHeader data-id="element-137">
                  <CardTitle className="text-2xl" data-id="element-138">
                    Request a Free Estimate
                  </CardTitle>
                  <CardDescription data-id="element-139">
                    Tell us a bit about your property and we'll get back to you
                    with a quote.
                  </CardDescription>
                </CardHeader>
                <CardContent data-id="element-140">
                  <form className="space-y-4" onSubmit={e => e.preventDefault()} data-id="element-141">
                    <div className="grid grid-cols-2 gap-4" data-id="element-142">
                      <div className="space-y-2" data-id="element-143">
                        <label className="text-sm font-medium" data-id="element-144">
                          First Name
                        </label>
                        <Input placeholder="John" className="bg-background/50 border-border/50 focus-visible:ring-primary/50" data-id="element-145" />
                      </div>
                      <div className="space-y-2" data-id="element-146">
                        <label className="text-sm font-medium" data-id="element-147">Last Name</label>
                        <Input placeholder="Doe" className="bg-background/50 border-border/50 focus-visible:ring-primary/50" data-id="element-148" />
                      </div>
                    </div>

                    <div className="space-y-2" data-id="element-149">
                      <label className="text-sm font-medium" data-id="element-150">
                        Email Address
                      </label>
                      <Input type="email" placeholder="john@example.com" className="bg-background/50 border-border/50 focus-visible:ring-primary/50" data-id="element-151" />
                    </div>

                    <div className="space-y-2" data-id="element-152">
                      <label className="text-sm font-medium" data-id="element-153">
                        Phone Number
                      </label>
                      <Input type="tel" placeholder="(555) 123-4567" className="bg-background/50 border-border/50 focus-visible:ring-primary/50" data-id="element-154" />
                    </div>

                    <div className="space-y-2" data-id="element-155">
                      <label className="text-sm font-medium" data-id="element-156">
                        Service Needed
                      </label>
                      <select className="flex h-10 w-full items-center justify-between rounded-md border border-border/50 bg-background/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-157">
                        <option value="" disabled selected data-id="element-158">
                          Select a service...
                        </option>
                        <option value="residential" data-id="element-159">
                          Residential Window Cleaning
                        </option>
                        <option value="commercial" data-id="element-160">
                          Commercial Window Cleaning
                        </option>
                        <option value="solar" data-id="element-161">Solar Panel Cleaning</option>
                        <option value="other" data-id="element-162">Other</option>
                      </select>
                    </div>

                    <div className="space-y-2" data-id="element-163">
                      <label className="text-sm font-medium" data-id="element-164">
                        Message (Optional)
                      </label>
                      <Textarea placeholder="Tell us about your home (approx. square footage, number of stories, etc.)" className="min-h-[100px] bg-background/50 border-border/50 focus-visible:ring-primary/50" data-id="element-165" />
                    </div>

                    <Button type="submit" className="w-full h-12 text-lg font-bold bg-primary hover:bg-primary/90 text-primary-foreground mt-2" data-id="element-166">
                      Submit Request
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-background border-t border-border/40 pt-16 pb-8" data-id="element-167">
        <div className="container mx-auto px-4" data-id="element-168">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12" data-id="element-169">
            <div className="md:col-span-2" data-id="element-170">
              <img src="https://cdn.magicpatterns.com/uploads/akHq6rnmt4As3JRquQqvbk/sparkle-tech-window-washing.png-1-e1707734280501.webp" alt="Sparkle Tech Window Washing Logo" className="h-12 w-auto object-contain brightness-150 contrast-125 mb-6" data-id="element-171" />
              <p className="text-muted-foreground max-w-sm mb-6" data-id="element-172">
                Premium window cleaning services for residential and commercial
                properties across the Phoenix metro area. Experience the
                difference of true professional care.
              </p>
              <div className="flex items-center gap-4" data-id="element-173">
                <a href="https://www.facebook.com/sparkletechwindowwashing" target="_blank" rel="noreferrer" className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-colors" data-id="element-174">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-175">
                    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" data-id="element-176" />
                  </svg>
                </a>
                <a href="https://www.instagram.com/sparkletechwindowwashing" target="_blank" rel="noreferrer" className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-colors" data-id="element-177">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-178">
                    <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" data-id="element-179" />
                  </svg>
                </a>
              </div>
            </div>

            <div data-id="element-180">
              <h4 className="font-semibold text-lg mb-4" data-id="element-181">Quick Links</h4>
              <ul className="space-y-3" data-id="element-182">
                <li data-id="element-183">
                  <a href="#services" className="text-muted-foreground hover:text-primary transition-colors" data-id="element-184">
                    Services
                  </a>
                </li>
                <li data-id="element-185">
                  <a href="#portfolio" className="text-muted-foreground hover:text-primary transition-colors" data-id="element-186">
                    Portfolio
                  </a>
                </li>
                <li data-id="element-187">
                  <a href="#about" className="text-muted-foreground hover:text-primary transition-colors" data-id="element-188">
                    About Us
                  </a>
                </li>
                <li data-id="element-189">
                  <a href="#quote" className="text-muted-foreground hover:text-primary transition-colors" data-id="element-190">
                    Get an Estimate
                  </a>
                </li>
              </ul>
            </div>

            <div data-id="element-191">
              <h4 className="font-semibold text-lg mb-4" data-id="element-192">Contact Info</h4>
              <ul className="space-y-3" data-id="element-193">
                <li className="flex items-start gap-2 text-muted-foreground" data-id="element-194">
                  <Phone className="h-5 w-5 shrink-0 text-primary" data-id="element-195" />
                  <a href="tel:18003703998" className="hover:text-primary transition-colors" data-id="element-196">
                    1-800-370-3998
                  </a>
                </li>
                <li className="flex items-start gap-2 text-muted-foreground" data-id="element-197">
                  <MapPin className="h-5 w-5 shrink-0 text-primary" data-id="element-198" />
                  <span data-id="element-199">
                    Peoria, AZ
                    <br data-id="element-200" />
                    Serving Phoenix Metro & Statewide
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border/40 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground" data-id="element-201">
            <p data-id="element-202">
              © {new Date().getFullYear()} Sparkle Tech Window Washing LLC. All
              rights reserved.
            </p>
            <div className="flex items-center gap-4" data-id="element-203">
              <span className="flex items-center gap-1" data-id="element-204">
                <ShieldCheck className="h-4 w-4" data-id="element-205" /> BBB Accredited Business
              </span>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}